// BABA Ledger — Sprint 1.4c upgrade: '+ Add cert' button + form,
// click row → drawer to edit / mark expired / replace document.

import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import DocumentUploader, { DocumentLink } from '../components/DocumentUploader';

type Project = { id: string; project_name: string; project_code: string };
type Vendor = { id: string; name: string; uei: string | null };
type Obligation = {
  id: string; code: string; name: string; cadence_rule: string;
  source_cite: string | null; due_date: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'accepted' | 'rejected' | 'overdue' | 'na';
};
type CurrentPrice = { id: string; line_item: string; category: string | null };
type BabaCert = {
  id: string; vendor_id: string; vendor_name: string; vendor_uei: string | null;
  cert_type: 'manufacturer_self_cert' | 'country_of_origin' | 'waiver' | 'dfars_compliance' | 'other';
  cert_reference: string | null;
  effective_date: string | null; expiry_date: string | null;
  status: 'active' | 'expired' | 'revoked' | 'pending';
  currency_flag: 'no_expiry' | 'current' | 'expiring_soon' | 'expired';
  material_code: string | null; material_name: string | null;
  document_id: string | null;
  notes: string | null;
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  submitted: { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  accepted: { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  rejected: { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue: { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na: { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
  active: { bg: '#d1fae5', fg: '#065f46', label: 'Active' },
  expired: { bg: '#fecaca', fg: '#7f1d1d', label: 'Expired' },
  revoked: { bg: '#fecaca', fg: '#7f1d1d', label: 'Revoked' },
};
const CURRENCY_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  current: { bg: '#d1fae5', fg: '#065f46', label: 'Current' },
  expiring_soon: { bg: '#fef3c7', fg: '#92400e', label: '< 30 days' },
  expired: { bg: '#fecaca', fg: '#7f1d1d', label: 'Expired' },
  no_expiry: { bg: '#e5e7eb', fg: '#6b7280', label: 'No expiry' },
};
const CERT_TYPE_LABEL: Record<string, string> = {
  manufacturer_self_cert: 'Manufacturer self-cert',
  country_of_origin: 'Country of origin',
  waiver: 'Waiver',
  dfars_compliance: 'DFARS',
  other: 'Other',
};

function Badge({ status, map }: { status: string; map: typeof STATUS_BADGE }) {
  const s = map[status] ?? STATUS_BADGE.pending;
  return <span style={{ background: s.bg, color: s.fg, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderTop: `3px solid ${color ?? '#3b82f6'}`, borderRadius: 6, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted, #64748b)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function BABALedgerPage() {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<BabaCert | null>(null);

  const { data: projects } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });
  const activeProjectId = projectId ?? projects?.[0]?.id ?? null;

  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: ['bead-vendors-list'],
    queryFn: () => api.get('/bead/vendors'),
  });

  const { data: obligations = [] } = useQuery<Obligation[]>({
    queryKey: ['mat-obligations', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/obligations?category=federal_materials`),
    enabled: !!activeProjectId,
  });

  const { data: certData } = useQuery<{ items: BabaCert[]; scoped: boolean }>({
    queryKey: ['baba-certs', activeProjectId],
    queryFn: () => api.get(`/bead/projects/${activeProjectId}/baba-certs`),
    enabled: !!activeProjectId,
  });
  const certs: BabaCert[] = certData?.items ?? [];
  const scoped = certData?.scoped ?? true;

  const kpis = useMemo(() => {
    const active = certs.filter(c => c.status === 'active').length;
    const expiringSoon = certs.filter(c => c.currency_flag === 'expiring_soon').length;
    const expired = certs.filter(c => c.currency_flag === 'expired' || c.status === 'expired').length;
    const waivers = certs.filter(c => c.cert_type === 'waiver').length;
    return { total: certs.length, active, expiringSoon, expired, waivers };
  }, [certs]);

  return (
    <div>
      <div className="page__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>BABA Ledger</h1>
          <div className="page__subtitle" style={{ color: 'var(--text-muted, #64748b)', fontSize: 13 }}>
            Build America Buy America — covered material certifications, waivers, and DFARS compliance
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={activeProjectId ?? ''} onChange={(e) => setProjectId(e.target.value || null)}
            style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border, #e2e8f0)', borderRadius: 6 }}>
            {(projects ?? []).map(p => <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>)}
          </select>
          <button onClick={() => setShowAdd(s => !s)} style={{
            padding: '6px 14px', background: '#0f172a', color: '#fff',
            border: '1px solid #0f172a', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>{showAdd ? '× Cancel' : '+ Add cert'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
        <StatCard label="Total certs" value={kpis.total} color="#3b82f6" />
        <StatCard label="Active" value={kpis.active} color="#10b981" />
        <StatCard label="Expiring < 30d" value={kpis.expiringSoon} color={kpis.expiringSoon > 0 ? '#f59e0b' : '#10b981'} />
        <StatCard label="Expired" value={kpis.expired} color={kpis.expired > 0 ? '#ef4444' : '#10b981'} />
        <StatCard label="Active waivers" value={kpis.waivers} color="#8b5cf6" />
      </div>

      {showAdd && <AddBabaCertForm vendors={vendors} onDone={() => setShowAdd(false)} />}

      {!scoped && (
        <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#92400e', marginBottom: 16 }}>
          Showing all BABA certs in the system — no project-scoped vendor → budget-line linkage matched.
        </div>
      )}

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>BABA obligations · {obligations.length}</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>{['Code','Requirement','Authority','Cadence','Due','Status'].map(h =>
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {obligations.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 16, color: '#94a3b8', textAlign: 'center' }}>No materials obligations on this project.</td></tr>
              ) : obligations.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#64748b' }}>{o.code}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{o.name}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.source_cite ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.cadence_rule.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.due_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><Badge status={o.status} map={STATUS_BADGE} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Certificates ledger · {certs.length}</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>{['Vendor','Material','Cert type','Reference','Doc','Effective','Expires','Currency','Status'].map(h =>
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {certs.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: 16, color: '#94a3b8', textAlign: 'center' }}>
                  No BABA certs on file. Click <b>+ Add cert</b> above.
                </td></tr>
              ) : certs.map(c => (
                <tr key={c.id} onClick={() => setEditing(c)}
                  style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                    {c.vendor_name}
                    {c.vendor_uei && <div style={{ fontSize: 10, color: '#94a3b8' }}>UEI {c.vendor_uei}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 12 }}>
                    {c.material_name ?? <span style={{ color: '#94a3b8' }}>—</span>}
                    {c.material_code && <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>{c.material_code}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 12 }}>{CERT_TYPE_LABEL[c.cert_type] ?? c.cert_type}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#64748b' }}>{c.cert_reference ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><DocumentLink documentId={c.document_id} label="cert" /></td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{c.effective_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{c.expiry_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><Badge status={c.currency_flag} map={CURRENCY_BADGE} /></td>
                  <td style={{ padding: '8px 10px' }}><Badge status={c.status} map={STATUS_BADGE} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {certs.length > 0 && (
            <div style={{ padding: '6px 10px', fontSize: 11, color: '#64748b', borderTop: '1px solid var(--border)', background: '#f8fafc' }}>
              Click any row to edit, replace the cert PDF, or mark expired/revoked.
            </div>
          )}
        </div>
      </section>

      {editing && <EditBabaCert cert={editing} projectId={activeProjectId!} onClose={() => setEditing(null)} />}
    </div>
  );
}

// --------------------------------------------------------------------
// Add new BABA cert
// --------------------------------------------------------------------

function AddBabaCertForm({ vendors, onDone }: { vendors: Vendor[]; onDone: () => void }) {
  const qc = useQueryClient();
  const [vendorId, setVendorId] = useState('');
  const [certType, setCertType] = useState<BabaCert['cert_type']>('manufacturer_self_cert');
  const [reference, setReference] = useState('');
  const [effective, setEffective] = useState('');
  const [expiry, setExpiry] = useState('');
  const [docId, setDocId] = useState<string | null>(null);
  const [priceId, setPriceId] = useState('');
  const [notes, setNotes] = useState('');

  const { data: prices = [] } = useQuery<CurrentPrice[]>({
    queryKey: ['bead-current-prices'],
    queryFn: () => api.get('/bead/costs').then((r: any) => r.items ?? r),
  });

  const createMut = useMutation({
    mutationFn: (b: any) => api.post(`/bead/vendors/${vendorId}/baba-certs`, b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['baba-certs'] });
      qc.invalidateQueries({ queryKey: ['bead-vendor', vendorId] });
      qc.invalidateQueries({ queryKey: ['bead-vendors'] });
      onDone();
    },
  });

  const inp: React.CSSProperties = { width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4 };
  const lbl: React.CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', marginBottom: 4, display: 'block' };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 6, padding: 16, marginBottom: 16 }}>
      <h3 style={{ margin: 0, fontSize: 14, marginBottom: 12 }}>New BABA cert</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Vendor</label>
          <select style={inp} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
            <option value="">Select vendor…</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Cert type</label>
          <select style={inp} value={certType} onChange={(e) => setCertType(e.target.value as any)}>
            {Object.entries(CERT_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Material (optional)</label>
          <select style={inp} value={priceId} onChange={(e) => setPriceId(e.target.value)}>
            <option value="">— any material —</option>
            {prices.slice(0, 100).map(p => <option key={p.id} value={p.id}>{p.line_item}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Cert reference</label>
          <input style={inp} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="MSC-2026-001" />
        </div>
        <div>
          <label style={lbl}>Effective date</label>
          <input type="date" style={inp} value={effective} onChange={(e) => setEffective(e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Expiry date</label>
          <input type="date" style={inp} value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <DocumentUploader value={docId} onChange={setDocId} vendorId={vendorId || undefined}
          docType="baba_cert" label="Cert PDF" hint="Upload the manufacturer-issued certification." />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Notes</label>
        <textarea style={{ ...inp, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
          value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button disabled={!vendorId || createMut.isPending}
          onClick={() => createMut.mutate({
            cert_type: certType,
            cert_reference: reference || null,
            current_price_id: priceId || null,
            effective_date: effective || null,
            expiry_date: expiry || null,
            document_id: docId,
            notes: notes || null,
          })}
          style={{
            padding: '6px 14px',
            background: !vendorId || createMut.isPending ? '#94a3b8' : '#0f172a',
            color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600,
            cursor: !vendorId || createMut.isPending ? 'not-allowed' : 'pointer',
          }}>{createMut.isPending ? 'Saving…' : 'Save cert'}</button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------
// Edit existing BABA cert (drawer)
// --------------------------------------------------------------------

function EditBabaCert({ cert, projectId, onClose }: { cert: BabaCert; projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(cert.status);
  const [reference, setReference] = useState(cert.cert_reference ?? '');
  const [effective, setEffective] = useState(cert.effective_date ?? '');
  const [expiry, setExpiry] = useState(cert.expiry_date ?? '');
  const [docId, setDocId] = useState<string | null>(cert.document_id);
  const [notes, setNotes] = useState(cert.notes ?? '');

  const updateMut = useMutation({
    mutationFn: (b: any) => api.put(`/bead/baba-certs/${cert.id}`, b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['baba-certs'] });
      onClose();
    },
  });
  const deleteMut = useMutation({
    mutationFn: () => api.del(`/bead/baba-certs/${cert.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['baba-certs'] });
      onClose();
    },
  });

  // Auto-save when uploader replaces the doc.
  useEffect(() => {
    if (docId !== cert.document_id) {
      updateMut.mutate({ document_id: docId });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 480, maxWidth: '100%', background: 'white', height: '100vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>{CERT_TYPE_LABEL[cert.cert_type]}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{cert.vendor_name}</div>
            {cert.material_name && <div style={{ fontSize: 11, color: '#64748b' }}>{cert.material_name}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
          <select value={status} onChange={(e) => setStatus(e.target.value as any)}
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }}>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Cert reference</div>
          <input value={reference} onChange={(e) => setReference(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'ui-monospace, monospace' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Effective</div>
            <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Expires</div>
            <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }} />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <DocumentUploader value={docId} onChange={setDocId}
            docType="baba_cert" label="Cert PDF" />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            style={{ width: '100%', minHeight: 60, padding: '6px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button onClick={() => { if (confirm('Delete this cert?')) deleteMut.mutate(); }}
            style={{ padding: '6px 14px', background: '#fff', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Delete cert
          </button>
          <button onClick={() => updateMut.mutate({
            status, cert_reference: reference || null,
            effective_date: effective || null, expiry_date: expiry || null,
            document_id: docId, notes: notes || null,
          })} disabled={updateMut.isPending}
            style={{ padding: '6px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {updateMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
